<script>
import Layout from '@layouts/main'
import { userMethods, authComputed } from '@state/helpers'
import _ from 'lodash'

export default {
  metaInfo: {
    title: 'OrgUsers',
    meta: [{ name: 'description', content: 'OrgUsers' }],
  },
  components: { Layout },
  data() {
    return {
      search: '',
      loadingError: null,
      formError: null,
      loading: true,
      dialog: false,
      pagination: {
        rowsPerPage: 25,
      },
      editedIndex: -1,
      items: [],
      totalData: 0,
      isWorking: false,
      headers: [
        {
          text: 'First Name',
          value: 'firstName',
        },
        {
          text: 'Last Name',
          value: 'lastName',
        },
        {
          text: 'Username',
          value: 'username',
        },
        {
          text: 'Active',
          value: 'active',
        },
        {
          text: 'Admin',
          value: 'isAdmin',
        },
        { text: 'Actions', value: 'firstName', sortable: false },
      ],
      editedItem: {
        firstName: null,
        lastName: null,
        username: null,
        isAdmin: false,
        active: false,
      },
      defaultItem: {
        firstName: null,
        lastName: null,
        username: null,
        isAdmin: false,
        active: false,
      },
    }
  },
  computed: {
    ...authComputed,
    formTitle() {
      return this.editedIndex === -1
        ? 'Invite New User'
        : `Edit ${this.items[this.editedIndex].username}`
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getDataFromApi()
      },
      deep: true,
    },
    dialog(val) {
      val || this.close()
    },
  },
  created() {
    this.getDataFromApi = _.debounce(this.getDataFromApi, 500)
  },
  methods: {
    ...userMethods,
    getDataFromApi() {
      this.loading = true
      var vm = this
      return this.getUsers({
        queryTerms: { ...this.pagination, filter: this.search },
      })
        .then(data => {
          vm.loading = false

          vm.totalData = data.totalCount
          vm.items = data.results
        })
        .catch(error => {
          vm.loading = false
          vm.loadingError = error
        })
    },
    save() {
      this.isWorking = true
      this.formError = null

      if (this.editedIndex !== -1) {
        // Update user
        this.updateUser({ user: this.editedItem })
          .then(response => {
            this.isWorking = false
            var user = response.data
            var matches = this.items.filter(x => x.id === user.id)
            if (matches.length) {
              var current = matches[0]
              var indexToReplace = this.items.indexOf(current)
              this.items[indexToReplace] = user
            }
            this.dialog = false
          })
          .catch(error => {
            this.isWorking = false
            this.formError = error.response.data
          })
      } else {
        // Add new
        this.inviteUser({ user: this.editedItem })
          .then(response => {
            this.isWorking = false
            var user = response.data
            this.items.unshift(user)
            this.dialog = false
          })
          .catch(error => {
            this.isWorking = false
            this.formError = error.response.data
          })
      }
    },
    close() {
      this.dialog = false
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      }, 300)
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },
  },
}
</script>

<template>
  <Layout>
    <v-container fluid grid-list-md ma-0>
      <v-toolbar flat color="transparent">
        <v-toolbar-title>Users</v-toolbar-title>
        <v-divider class="mx-2" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" max-width="500px">
          <v-btn slot="activator" color="primary" dark class="mb-2"
            >Invite User</v-btn
          >
          <v-card>
            <v-card-title>
              <span class="headline">{{ formTitle }}</span>
            </v-card-title>

            <v-card-text>
              <v-container grid-list-md>
                <v-layout wrap>
                  <v-flex xs12 sm12 md12>
                    <v-text-field
                      v-if="!editedItem.id"
                      v-model="editedItem.username"
                      type="email"
                      label="Email Address"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm6 md4>
                    <v-text-field
                      v-model="editedItem.firstName"
                      label="First Name"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm6 md4>
                    <v-text-field
                      v-model="editedItem.lastName"
                      label="Last Name"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm6 md4>
                    <v-checkbox
                      v-if="editedItem.id && currentUser.id !== editedItem.id"
                      v-model="editedItem.active"
                      label="Active"
                    ></v-checkbox>
                  </v-flex>
                  <v-flex xs12 sm6 md4>
                    <v-checkbox
                      v-model="editedItem.isAdmin"
                      label="Admin"
                    ></v-checkbox>
                  </v-flex>
                  <v-alert :value="formError" type="error">
                    {{ formError }}
                  </v-alert>
                </v-layout>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" flat @click="close">Cancel</v-btn>
              <v-btn color="blue darken-1" flat @click="save">
                <BaseIcon v-if="isWorking" name="sync" spin />
                <span v-else>Save</span>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-toolbar>
      <v-card>
        <v-card-title>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="items"
          :pagination.sync="pagination"
          :total-items="totalData"
          :loading="loading"
          :search="search"
          class="elevation-1"
        >
          <v-progress-linear
            slot="progress"
            color="blue"
            indeterminate
          ></v-progress-linear>
          <template slot="items" slot-scope="props">
            <td>{{ props.item.firstName }}</td>
            <td>{{ props.item.lastName }}</td>
            <td>{{ props.item.username }}</td>
            <td>
              <v-icon v-if="props.item.active" color="green">mdi-check</v-icon>
              <v-icon v-else color="red">mdi-close</v-icon>
            </td>
            <td>
              <v-icon v-if="props.item.isAdmin" color="green">mdi-check</v-icon>
              <v-icon v-else color="red">mdi-close</v-icon>
            </td>
            <td>
              <v-icon small class="mr-2" @click="editItem(props.item)"
                >edit</v-icon
              >
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
</style>
